import { useCategories } from '@/entities/categories/composables/useCategories';
import type { IBreadcrumbs } from '@/shared/ui/Breadcrumbs/types';
import type { ProductsQueryParams, Product } from '@/entities/products/model/types/product';
import type { Category } from '@/entities/categories/model/types/categories';

export const useCatalogDetail = async () => {
  const route = useRoute();
  const router = useRouter();
  const { t, locale } = useI18n();
  const localePath = useLocalePath();
  const { $productsService } = useNuxtApp();
  const { category, subcategories } = useCategories();
  const currentPage: Ref<number> = ref(Number(route.query.page) || 1);
  const total: Ref<number> = ref(0);
  const perPage: Ref<number> = ref(12);
  const appendMode: Ref<boolean> = ref(false);
  const products: Ref<Product[]> = ref([]);

  const queryParams = computed(() => {
    const params: ProductsQueryParams = {};

    type ParamTransformer = (value: string) => Partial<ProductsQueryParams>;

    const paramTransformers: Record<string, ParamTransformer> = {
      subcategory: (value: string) => ({ category_slug: value }),
      category: (value: string) => ({ category_slug: value }),
      popular: (value: string) => ({ popular: value === 'true' }),
      ordering: (value: string) => ({ ordering: value }),
      new_arrival: (value: string) => ({ new_arrival: value === 'true' }),
      action: (value: string) => ({ action: value === 'true' }),
      sale: (value: string) => ({ sale: value === 'true' }),
      liquidation: (value: string) => ({ liquidation: value === 'true' }),
      min_price: (value: string) => ({ min_price: Number(value) }),
      max_price: (value: string) => ({ max_price: Number(value) }),
      brand: (value: string) => ({ brand: value.split(',').map(Number) })
    };

    Object.entries({ ...route.params, ...route.query })
      .filter(([key]) => key in paramTransformers)
      .forEach(([key, value]) => {
        if (value) {
          Object.assign(params, paramTransformers[key](String(value)));
        }
      });

    return params;
  });

  const title: Ref<string> = computed(() => {
    if (route.params.subcategory) {
      const subcategory = subcategories.value.find(
        (sub: Category) => sub[`slug_${locale.value}`] === route.params.subcategory
      );
      return subcategory?.name || category.value?.name || '';
    }
    return category.value?.name || '';
  });

  const breadcrumbs: Ref<IBreadcrumbs[]> = computed(() => {
    const subcategory = subcategories.value.find(sub => sub[`slug_${locale.value}`] === route.params.subcategory);
    const categoryLink = subcategory && category.value ? localePath(`/catalog/${category.value[`slug_${locale.value}`]}`) : '';

    const arr: IBreadcrumbs[] = [
      { id: 1, text: t('main'), link: localePath('/') },
      { id: 2, text: t('catalog'), link: localePath('catalog') },
      { id: 3, text: category.value?.name || '', link: categoryLink }
    ];

    if (subcategory) {
      arr.push({ id: 4, text: subcategory.name });
    }

    return arr;
  });

  const changeCurrentPage = async (page: number, type: 'change' | 'load-more') => {
    appendMode.value = type === 'load-more';
    currentPage.value = page;

    router.push({
      path: route.path,
      query: { ...route.query, page },
    });
  };

  const isShowPagination = computed(() => {
    return total.value > perPage.value;
  });

  const totalPages = computed(() => {
    return Math.ceil(total.value / perPage.value);
  });


  const { data } = await useAsyncData('catalog-products', async () => {
    const res = await $productsService.getAllProducts({...queryParams.value, page: currentPage.value, page_size: perPage.value });
    return res.data || [];
  }, { watch: [currentPage] });


  watch(data, (res) => {
    if (!res) return

    const items = res.results || []

    if (appendMode.value) {
      products.value.push(...items)
    } else {
      products.value = items
    }

    total.value = res.count || 0;
    appendMode.value = false

  }, { deep: true, immediate: true })

  watch(route, (newValue) => {
    currentPage.value = Number(newValue.query.page) || 1;
  }, { deep: true })

  useSeoMeta({
    title: title.value,
    ogTitle: title.value,
    description: t('catalogDetailDescription'),
    ogDescription: t('catalogDetailDescription'),
    ogImage: 'https://resursi-distribution.uz/images/banners/catalog/catalog-banner.png',
  });

  return {
    category,
    subcategories,
    currentPage,
    title,
    breadcrumbs,
    totalPages,
    catalogProducts: products,
    isShowPagination,
    changeCurrentPage,
  };
};
